import { Tabs, TabsContent, TabsList, TabsTrigger } from '@staff-app/ui';

import { useAuthStore } from '@/stores/auth-store';
import { Laptop } from 'lucide-react';

/**
 * Default agent download links — DigitalOcean Spaces (`download-optiedge-com`,
 * nyc3), the same bucket the macOS auto-updater reads. Overridable per-env via
 * VITE_OPTIEDGE_AGENT_DOWNLOAD_URL* vars.
 *
 * These point at the mutable `staff/latest/` prefix, NOT a versioned path, so a
 * release only has to overwrite the object — this file never changes. That's the
 * property the old Google Drive links had via "Manage versions → Upload new
 * version"; Drive is no longer maintained. Immutable per-release copies live
 * alongside at `staff/v<version>-staff-alpha/` and are what the signed
 * update manifest pins by sha256.
 *
 * Origin host (not the .cdn. alias) on purpose: the DO CDN overrides object
 * Cache-Control with the Space's edge TTL, which would serve a stale binary
 * after a release. Origin is always fresh.
 */
const DOWNLOAD_BASE =
  'https://download-optiedge-com.nyc3.digitaloceanspaces.com/staff/latest';
const DEFAULT_DOWNLOAD_URL_MACOS = `${DOWNLOAD_BASE}/OptiEdge-staff-Mac.pkg`;
const DEFAULT_DOWNLOAD_URL_WINDOWS = `${DOWNLOAD_BASE}/OptiEdge-staff-Win.exe`;
const DEFAULT_DOWNLOAD_URL_LINUX_AMD64 = `${DOWNLOAD_BASE}/OptiEdge-staff-Linux-amd64`;
const DEFAULT_DOWNLOAD_URL_LINUX_ARM64 = `${DOWNLOAD_BASE}/OptiEdge-staff-Linux-arm64`;

/**
 * EnrollLaptopPage — "My Account" → "Enroll my laptop"
 *
 * Staff-facing landing page for the optiedge agent. Shows the
 * currently-signed-in staff member their subnet/role mapping, then a
 * per-platform (macOS / Windows / Linux) install guide in tabs.
 *
 * Platform facts the copy below relies on (keep in sync with optiedge-agent):
 *   - macOS: signed .pkg → LaunchDaemon (tunnel) + LaunchAgent (menu-bar UI),
 *     browser opens at 127.0.0.1:8088 for Google sign-in.
 *   - Windows: OptiEdge-staff-setup-<v>.exe (NSIS, bundles wintun.dll) →
 *     installs to Program Files, service auto-starts, browser opens for
 *     sign-in. Not Authenticode-signed yet — SmartScreen warns.
 *   - Linux: no packaged installer; the binary installs itself as a systemd
 *     service (`install-service`) so the tunnel survives reboot — sandbox +
 *     prod come up on wg0/wg1 (v0.2.1+), sign in at 127.0.0.1:8088.
 *     Proxy/full-tunnel mode is macOS-only.
 */

export function EnrollLaptopPage() {
  const adminUser = useAuthStore((s) => s.adminUser);

  const roles = adminUser?.roles ?? [];
  const primaryRole = roles.find((r) => r === 'root' || r === 'engineer' || r === 'support') ?? roles[0] ?? '';
  const email = adminUser?.email ?? '';
  const roleDisplay = roles.join(', ');
  const subnet = roleToSubnet(primaryRole);

  const macUrl =
    import.meta.env.VITE_OPTIEDGE_AGENT_DOWNLOAD_URL ?? DEFAULT_DOWNLOAD_URL_MACOS;
  const winUrl =
    import.meta.env.VITE_OPTIEDGE_AGENT_DOWNLOAD_URL_WINDOWS ?? DEFAULT_DOWNLOAD_URL_WINDOWS;
  const linuxAmd64Url =
    import.meta.env.VITE_OPTIEDGE_AGENT_DOWNLOAD_URL_LINUX_AMD64 ??
    DEFAULT_DOWNLOAD_URL_LINUX_AMD64;
  const linuxArm64Url =
    import.meta.env.VITE_OPTIEDGE_AGENT_DOWNLOAD_URL_LINUX_ARM64 ??
    DEFAULT_DOWNLOAD_URL_LINUX_ARM64;

  return (
    <div className="mx-auto max-w-3xl space-y-8 p-8">
      <header className="space-y-2">
        <h1 className="flex items-center gap-2 text-2xl font-bold text-foreground">
          <Laptop className="h-6 w-6" />
          {'Enroll my laptop'}
        </h1>
        <p className="text-sm text-muted-foreground">
          {
            'Install the OptiEdge agent on your laptop to join the OptiSigns corporate VPN. Once connected, you can SSH into customer devices to troubleshoot.'
          }
        </p>
      </header>

      <section className="rounded-lg border bg-muted/30 p-6" data-testid="enroll-identity">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {'Signed in as'}
        </h2>
        <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <dt className="text-muted-foreground">{'Email'}</dt>
          <dd className="font-mono text-foreground">{email || '—'}</dd>
          <dt className="text-muted-foreground">{'Role'}</dt>
          <dd className="font-mono text-foreground" data-testid="enroll-role">{roleDisplay || '—'}</dd>
          <dt className="text-muted-foreground">{'Will join subnet'}</dt>
          <dd className="font-mono text-foreground" data-testid="enroll-subnet">{subnet}</dd>
        </dl>
      </section>

      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-foreground">{'Setup'}</h2>

        <Tabs defaultValue="macos" data-testid="enroll-platform-tabs">
          <TabsList>
            <TabsTrigger value="macos" data-testid="tab-macos">
              {'macOS'}
            </TabsTrigger>
            <TabsTrigger value="windows" data-testid="tab-windows">
              {'Windows'}
            </TabsTrigger>
            <TabsTrigger value="linux" data-testid="tab-linux">
              {'Linux'}
            </TabsTrigger>
          </TabsList>

          {/* ------------------------------ macOS ------------------------------ */}
          <TabsContent value="macos" className="pt-2" data-testid="content-macos">
            <ol className="space-y-5">
              <Step
                n={1}
                title={'Download the agent'}
                body={
                  <>
                    <p className="text-sm text-muted-foreground">
                      {'Signed macOS installer (.pkg) — latest production build.'}
                    </p>
                    <DownloadButton
                      url={macUrl}
                      label={'Download OptiEdge-staff-Mac.pkg'}
                      testId="download-agent"
                    />
                  </>
                }
              />
              <Step
                n={2}
                title={'Install + sign in'}
                body={
                  <p className="text-sm text-muted-foreground">
                    {
                      'Double-click the .pkg and follow the installer (enter your Mac password when prompted). When it finishes, the OptiEdge icon appears in your menu bar and a browser tab opens automatically — sign in with your @optisigns.com Google account to join the VPN.'
                    }
                  </p>
                }
              />
              <Step
                n={3}
                title={'Verify the tunnel'}
                body={
                  <p className="text-sm text-muted-foreground">
                    {
                      "When the menu-bar icon shows ● Connected for each environment, you're on the VPN and can SSH into customer devices. Any personal WG tunnel stays untouched on a separate utun interface."
                    }
                  </p>
                }
              />
            </ol>
          </TabsContent>

          {/* ----------------------------- Windows ----------------------------- */}
          <TabsContent value="windows" className="pt-2" data-testid="content-windows">
            <ol className="space-y-5">
              <Step
                n={1}
                title={'Download the installer'}
                body={
                  <>
                    <p className="text-sm text-muted-foreground">
                      {'Windows installer (OptiEdge-staff-Win.exe) — 64-bit, bundles the wintun driver.'}
                    </p>
                    <DownloadButton
                      url={winUrl}
                      label={'Download OptiEdge-staff-Win.exe'}
                      testId="download-agent-windows"
                    />
                  </>
                }
              />
              <Step
                n={2}
                title={'Run the setup as administrator'}
                body={
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <p>
                      {
                        'Right-click OptiEdge-staff-Win.exe → "Run as administrator". The installer copies the agent to Program Files, registers the OptiEdge service, and starts it automatically.'
                      }
                    </p>
                    <p>
                      {
                        'The installer is not code-signed yet, so Windows SmartScreen may warn — click "More info" → "Run anyway".'
                      }
                    </p>
                  </div>
                }
              />
              <Step
                n={3}
                title={'Sign in + verify'}
                body={
                  <p className="text-sm text-muted-foreground">
                    {
                      'A browser tab opens automatically after install — sign in with your @optisigns.com Google account. When both environment cards show "live", the tunnel is up. To re-open the status page later, visit '
                    }
                    <Code>{'http://127.0.0.1:8088/welcome'}</Code>
                    {'.'}
                  </p>
                }
              />
            </ol>
          </TabsContent>

          {/* ------------------------------ Linux ------------------------------ */}
          <TabsContent value="linux" className="pt-2" data-testid="content-linux">
            <ol className="space-y-5">
              <Step
                n={1}
                title={'Download the binary for your CPU'}
                body={
                  <>
                    <p className="text-sm text-muted-foreground">
                      {
                        'Linux has no packaged installer — download the standalone binary matching your CPU architecture (check with '
                      }
                      <Code>{'uname -m'}</Code>
                      {': x86_64 → amd64, aarch64 → arm64); the next step installs it as a systemd service.'}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <DownloadButton
                        url={linuxAmd64Url}
                        label={'Download OptiEdge-staff-Linux-amd64'}
                        testId="download-agent-linux-amd64"
                      />
                      <DownloadButton
                        url={linuxArm64Url}
                        label={'Download OptiEdge-staff-Linux-arm64'}
                        testId="download-agent-linux-arm64"
                      />
                    </div>
                  </>
                }
              />
              <Step
                n={2}
                title={'Install it as a boot service'}
                body={
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <p>
                      {'Root is required — this copies the binary to a stable path and registers a systemd service that owns the WireGuard TUN interfaces (swap '}
                      <Code>{'amd64'}</Code>
                      {' for '}
                      <Code>{'arm64'}</Code>
                      {' if that’s your build):'}
                    </p>
                    <CodeBlock>
                      {'chmod +x ./OptiEdge-staff-Linux-amd64\nsudo install -m 0755 ./OptiEdge-staff-Linux-amd64 /usr/local/bin/optiedge\nsudo optiedge install-service\nsudo systemctl start com.optisigns.optiedge'}
                    </CodeBlock>
                    <p>
                      {'The service auto-starts on every reboot and self-restarts on failure — unlike '}
                      <Code>{'optiedge up'}</Code>
                      {', which stops when you close the terminal. Once it’s running, open '}
                      <Code>{'http://127.0.0.1:8088/enroll'}</Code>
                      {' and sign in with your @optisigns.com Google account. Sandbox and prod tunnels come up together on wg0 + wg1.'}
                    </p>
                  </div>
                }
              />
              <Step
                n={3}
                title={'Verify + tear down'}
                body={
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <p>
                      {'Check service + tunnel state any time with '}
                      <Code>{'optiedge service-status'}</Code>
                      {', at '}
                      <Code>{'http://127.0.0.1:8088/welcome'}</Code>
                      {', or with '}
                      <Code>{'sudo wg show'}</Code>
                      {'. To stop the tunnel and remove the boot service:'}
                    </p>
                    <CodeBlock>{'sudo optiedge uninstall-service'}</CodeBlock>
                    <p>
                      {
                        'Note: proxy/full-tunnel mode is macOS-only for now — Linux runs split-tunnel (VPN subnets only).'
                      }
                    </p>
                  </div>
                }
              />
            </ol>
          </TabsContent>
        </Tabs>
      </section>

      <footer className="rounded-lg border bg-muted/30 p-4 text-xs text-muted-foreground">
        <p>
          <strong>{'How this maps to the VPN:'}</strong>{' '}
          {
            'Your peer records are created in the sandbox (100.127.0.0/16) and prod (100.64.0.0/10) overlays. Your email and role are attached to each peer — the staff console shows them in the Network → Peers view.'
          }
        </p>
      </footer>
    </div>
  );
}

interface StepProps {
  n: number;
  title: string;
  body: React.ReactNode;
}

function Step({ n, title, body }: StepProps) {
  return (
    <li className="flex gap-4">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
        {n}
      </div>
      <div className="flex-1 space-y-1">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        {body}
      </div>
    </li>
  );
}

/**
 * DownloadButton — primary download CTA, or an amber "not published yet"
 * note when the artifact has no URL configured for this env.
 */
function DownloadButton({
  url,
  label,
  testId,
}: {
  url: string;
  label: string;
  testId: string;
}) {
  if (!url) {
    return (
      <p
        className="mt-2 inline-block rounded-md border border-warning/40 bg-warning-bg px-3 py-2 text-xs text-warning"
        data-testid={`${testId}-unavailable`}
      >
        {'Build not published yet — ask in the #release channel for the latest build.'}
      </p>
    );
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="mt-2 inline-block rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
      data-testid={testId}
    >
      {label}
    </a>
  );
}

function Code({ children }: { children: string }) {
  return (
    <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-foreground">
      {children}
    </code>
  );
}

function CodeBlock({ children }: { children: string }) {
  return (
    <pre className="overflow-x-auto rounded-md bg-muted p-3 font-mono text-xs leading-relaxed text-foreground">
      {children}
    </pre>
  );
}

/**
 * roleToSubnet — client-side display copy of the server-side map in
 * staff-enroll.service.ts. Shown here so staff know which subnet they'll
 * land on BEFORE they run the agent. Must stay in sync with the server map:
 *   - root, engineer → engineers
 *   - support, readonly, member → support
 */
function roleToSubnet(role: string): string {
  switch (role) {
    case 'root':
    case 'engineer':
      return 'engineers';
    case 'support':
    case 'readonly':
      return 'support';
    default:
      return '—';
  }
}
